/**
 * ServiceRequestModal — modal fired from CompanyDetail that lets the merchant
 * open a "طلب إدراج خدمة" against a linked company. The modal:
 *   1. Fetches the admin service catalog (main → sub).
 *   2. Lets the merchant pick main → sub.
 *   3. Collects optional notes + attachments (uploaded AFTER create).
 *   4. POSTs the create request; on success, navigates to the detail page.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, X, Briefcase, ChevronDown, Paperclip,
  Send, FileText, AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServiceRequestModal({ open, onClose, company, language = 'ar', isDarkMode = true }) {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [mainId, setMainId] = useState('');
  const [subId, setSubId] = useState('');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState([]); // {file, name, size}[]
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMainId(''); setSubId(''); setNotes(''); setAttachments([]);
    setCatalogError(null); setLoadingCatalog(true);
    const token = localStorage.getItem('business_token');
    axios.get(`${BACKEND_URL}/api/service-requests/catalog`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => setCatalog(r.data?.data || []))
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setCatalogError(typeof detail === 'string'
          ? detail
          : (language === 'ar' ? 'تعذّر جلب قائمة الخدمات' : 'Could not load services'));
      })
      .finally(() => setLoadingCatalog(false));
  }, [open, language]);

  const subs = useMemo(() => {
    const main = catalog.find((m) => m.id === mainId);
    return main?.subs || [];
  }, [catalog, mainId]);

  const canSubmit = !submitting && mainId && subId && company?.id;

  const addFiles = (fileList) => {
    const next = [...attachments];
    for (const f of Array.from(fileList || [])) {
      if (f.size > 8 * 1024 * 1024) {
        toast.error(language === 'ar' ? `${f.name} أكبر من 8 ميجا` : `${f.name} is over 8 MB`);
        continue;
      }
      next.push({ file: f, name: f.name, size: f.size });
    }
    setAttachments(next);
  };

  const removeFile = (idx) => {
    setAttachments(attachments.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const token = localStorage.getItem('business_token');
    try {
      const createRes = await axios.post(
        `${BACKEND_URL}/api/service-requests`,
        {
          company_id: company.id,
          main_service_id: mainId,
          sub_service_id: subId,
          notes: notes.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = createRes.data;
      const requestId = created?.id || created?.request_id;

      // Upload attachments sequentially so we can show per-file errors.
      for (const a of attachments) {
        try {
          const fd = new FormData();
          fd.append('file', a.file, a.name);
          await axios.post(
            `${BACKEND_URL}/api/service-requests/${requestId}/attachments`,
            fd,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
        } catch (upErr) {
          toast.error(language === 'ar'
            ? `فشل رفع ${a.name}`
            : `Failed to upload ${a.name}`);
        }
      }

      toast.success(language === 'ar'
        ? `تم استلام طلبك رقم ${created?.request_no || ''}`
        : `Request ${created?.request_no || ''} received`);
      onClose && onClose();
      if (requestId) navigate(`/service-requests/${requestId}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string'
        ? detail
        : (language === 'ar' ? 'فشل إرسال الطلب' : 'Request submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="service-request-modal"
    >
      <div className={`relative w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border ${
        isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'
      }`}>
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between gap-3 p-4 border-b border-inherit bg-gradient-to-r from-[#38BDF8]/15 to-[#F97316]/15 backdrop-blur">
          <div className="flex items-center gap-3 min-w-0">
            <div className="cgm-icon-glow !p-2 !w-9 !h-9 flex-shrink-0">
              <Briefcase className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-black text-base" style={{ fontFamily: 'Cairo, sans-serif' }}>
                {language === 'ar' ? 'طلب إدراج خدمة' : 'Request a Service'}
              </h3>
              <p className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {language === 'ar'
                  ? (company?.company_name_ar || company?.company_name_en)
                  : (company?.company_name_en || company?.company_name_ar)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 rounded-lg hover:bg-white/10 transition-colors`}
            data-testid="service-request-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {loadingCatalog && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[#38BDF8]" />
            </div>
          )}

          {!loadingCatalog && catalogError && (
            <div className={`rounded-lg border p-3 flex items-start gap-2 ${
              isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
            }`}>
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-500">{catalogError}</p>
            </div>
          )}

          {!loadingCatalog && !catalogError && (
            <>
              {/* Main service dropdown */}
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'الخدمة الرئيسية' : 'Main service'}
                </label>
                <div className="relative">
                  <select
                    value={mainId}
                    onChange={(e) => { setMainId(e.target.value); setSubId(''); }}
                    className={`w-full appearance-none rounded-lg border px-3 py-2.5 pe-9 text-sm transition-colors ${
                      isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'
                    } focus:border-[#38BDF8] focus:outline-none`}
                    data-testid="service-main-select"
                  >
                    <option value="">— {language === 'ar' ? 'اختر' : 'Select'} —</option>
                    {catalog.map((m) => (
                      <option key={m.id} value={m.id}>
                        {language === 'ar' ? (m.name_ar || m.name_en) : (m.name_en || m.name_ar)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none opacity-50" />
                </div>
              </div>

              {/* Sub service dropdown */}
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'الخدمة الفرعية' : 'Sub-service'}
                </label>
                <div className="relative">
                  <select
                    value={subId}
                    onChange={(e) => setSubId(e.target.value)}
                    disabled={!mainId}
                    className={`w-full appearance-none rounded-lg border px-3 py-2.5 pe-9 text-sm transition-colors ${
                      isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'
                    } focus:border-[#38BDF8] focus:outline-none disabled:opacity-50`}
                    data-testid="service-sub-select"
                  >
                    <option value="">— {language === 'ar' ? 'اختر' : 'Select'} —</option>
                    {subs.map((s) => (
                      <option key={s.id} value={s.id}>
                        {language === 'ar' ? (s.name_ar || s.name_en) : (s.name_en || s.name_ar)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none opacity-50" />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={language === 'ar' ? 'أضف أي تفاصيل تساعد الفريق على تنفيذ طلبك…' : 'Any detail that helps us execute your request…'}
                  className={`w-full rounded-lg border px-3 py-2.5 text-sm resize-none transition-colors ${
                    isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
                  } focus:border-[#38BDF8] focus:outline-none`}
                  data-testid="service-notes"
                />
              </div>

              {/* Attachments */}
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'المرفقات (حتى 8MB لكل ملف)' : 'Attachments (max 8MB each)'}
                </label>
                <label
                  htmlFor="svc-req-file"
                  className={`flex items-center gap-2 cursor-pointer rounded-lg border border-dashed px-4 py-3 text-sm transition-colors ${
                    isDarkMode ? 'border-white/20 hover:border-[#38BDF8]/60 text-slate-300' : 'border-slate-300 hover:border-[#38BDF8] text-slate-600'
                  }`}
                >
                  <Paperclip className="h-4 w-4" />
                  {language === 'ar' ? 'اختر ملفات أو اسحبها هنا' : 'Choose files or drop here'}
                  <input
                    id="svc-req-file"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => addFiles(e.target.files)}
                    data-testid="service-attachments-input"
                  />
                </label>
                {attachments.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {attachments.map((a, i) => (
                      <li key={i} className={`flex items-center justify-between gap-2 text-xs rounded-md px-2 py-1 ${
                        isDarkMode ? 'bg-white/5' : 'bg-slate-100'
                      }`}>
                        <span className="flex items-center gap-1.5 min-w-0">
                          <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{a.name}</span>
                          <span className="text-slate-500 flex-shrink-0">({Math.round(a.size / 1024)}KB)</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="text-slate-500 hover:text-red-500"
                          aria-label="remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 flex items-center justify-end gap-2 p-4 border-t border-inherit ${isDarkMode ? 'bg-[#0F172A]/95' : 'bg-white/95'} backdrop-blur`}>
          <button
            type="button"
            onClick={onClose}
            className={`cgm-btn-secondary`}
            data-testid="service-request-cancel"
          >
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="cgm-cta-primary disabled:opacity-50 disabled:pointer-events-none"
            data-testid="service-request-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {language === 'ar' ? 'إرسال الطلب' : 'Submit request'}
          </button>
        </div>
      </div>
    </div>
  );
}
